import { sfx, setSoundEnabled } from "./audio/sfx";
import { DIFFICULTIES } from "./config";
import { computeGridSize } from "./core/grid";
import { transitionFromGameOver, transitionFromPause, transitionFromTitle } from "./core/stateMachine";
import { createPlay, enqueueDirection, tickIntervalMs, tickPlay, type PlayState } from "./core/game";
import type { GameAction, GameOptions, Screen } from "./core/types";
import { createInputManager, type InputManager } from "./input/inputManager";
import { Renderer } from "./render/renderer";
import { loadHiScore, saveHiScore } from "./score";
import { applySettingsAction, createDefaultSettings, difficultyOf, navigateMenu, type GameSettings } from "./ui/settings";
import { drawTitle } from "./render/views/titleView";
import { drawSettings } from "./render/views/settingsView";
import { drawGame, drawPause } from "./render/views/gameView";
import { drawGameOver } from "./render/views/gameOverView";

/**
 * Uygulamanın orkestratörü: ekran durumu, ayarlar ve oyun döngüsünü bir arada tutar.
 * Mantık `core/`'da, çizim `render/`'da, giriş `input/`'da — burada sadece kablolama var.
 */
class SnakeGame {
  private readonly renderer: Renderer;
  private readonly input: InputManager;
  private readonly settings: GameSettings = createDefaultSettings();

  private screen: Screen = "title";
  private menuIndex = 0;
  private play: PlayState | null = null;
  private playOpts: GameOptions | null = null;
  private hiScore = loadHiScore();
  private tickAcc = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas, computeGridSize(this.aspect()));
    this.input = createInputManager((action) => this.handleAction(action));
  }

  dispose(): void {
    this.input.dispose();
  }

  /** Gamepad durumunu tarar; her karede çağrılır. */
  poll(now: number): void {
    this.input.poll(now);
  }

  /** Pencere boyutu değişti. Oyun sırasında grid sabit kalır, yalnızca ölçeklenir. */
  resize(): void {
    const inPlay = this.screen === "game" || this.screen === "pause" || this.screen === "over";
    const grid = this.play && inPlay ? this.play.grid : computeGridSize(this.aspect());
    this.renderer.resize(grid);
  }

  update(dtMs: number): void {
    if (this.screen !== "game" || !this.play || !this.playOpts) return;
    this.tickAcc += dtMs;
    while (this.screen === "game" && this.tickAcc >= tickIntervalMs(this.playOpts, this.play.foodEaten)) {
      this.tickAcc -= tickIntervalMs(this.playOpts, this.play.foodEaten);
      if (tickPlay(this.play, this.playOpts) === "dead") this.gameOver();
    }
  }

  draw(padConnected: boolean): void {
    const rc = { ctx: this.renderer.context, layout: this.renderer.view };
    this.renderer.drawBackground();

    switch (this.screen) {
      case "title":
        drawTitle(rc, padConnected);
        break;
      case "settings":
        drawSettings(rc, this.settings, this.menuIndex);
        break;
      case "game":
        if (this.play) drawGame(rc, this.play, this.gameViewData(), this.hiScore);
        break;
      case "pause":
        if (this.play) {
          drawGame(rc, this.play, this.gameViewData(), this.hiScore);
          drawPause(rc);
        }
        break;
      case "over":
        if (this.play) drawGameOver(rc, this.play, this.settings.wallPass, this.hiScore);
        break;
    }
  }

  private gameViewData() {
    return {
      difficultyLabel: difficultyOf(this.settings).label,
      wallPass: this.settings.wallPass,
      sound: this.settings.sound,
    };
  }

  private aspect(): number {
    return window.innerWidth / window.innerHeight;
  }

  private handleAction(action: GameAction): void {
    if (action === "fullscreen") {
      toggleFullscreen();
      return;
    }
    if (action === "sound") {
      this.settings.sound = !this.settings.sound;
      setSoundEnabled(this.settings.sound);
      sfx.menu();
      return;
    }

    switch (this.screen) {
      case "title":
        this.onTitle(action);
        break;
      case "settings":
        this.onSettings(action);
        break;
      case "game":
        this.onGame(action);
        break;
      case "pause":
        this.screen = transitionFromPause(action) ?? this.screen;
        if (this.screen === "game" || this.screen === "title") sfx.menu();
        break;
      case "over":
        this.onGameOver(action);
        break;
    }
  }

  private onTitle(action: GameAction): void {
    const next = transitionFromTitle(action);
    if (next) {
      this.screen = next;
      sfx.select();
    }
  }

  private onSettings(action: GameAction): void {
    if (action === "back") {
      this.screen = "title";
      sfx.menu();
      return;
    }
    if (action === "up" || action === "down") {
      this.menuIndex = navigateMenu(this.menuIndex, action === "up" ? -1 : 1);
      sfx.menu();
      return;
    }
    if (this.menuIndex === 4 && action === "ok") {
      this.startGame();
      return;
    }
    applySettingsAction(this.menuIndex, action, this.settings);
    if (action === "left" || action === "right" || action === "ok") sfx.menu();
  }

  private onGame(action: GameAction): void {
    if (!this.play) return;
    if (action === "up" || action === "down" || action === "left" || action === "right") {
      enqueueDirection(this.play, action);
      return;
    }
    if (action === "pause" || action === "back") {
      this.screen = "pause";
      sfx.menu();
    }
  }

  private onGameOver(action: GameAction): void {
    const next = transitionFromGameOver(action);
    if (!next) return;
    if (next === "game") {
      this.startGame();
      return;
    }
    this.screen = next;
    sfx.menu();
  }

  private startGame(): void {
    const grid = computeGridSize(this.aspect());
    this.play = createPlay(grid);
    this.playOpts = {
      baseTickMs: DIFFICULTIES[this.settings.difficultyIndex].tick,
      wallPass: this.settings.wallPass,
      progressive: this.settings.progressive,
    };
    this.tickAcc = 0;
    this.screen = "game";
    sfx.start();
  }

  private gameOver(): void {
    if (!this.play) return;
    this.screen = "over";
    sfx.die();
    if (this.play.score > this.hiScore) {
      this.hiScore = this.play.score;
      saveHiScore(this.hiScore);
    }
  }
}

function toggleFullscreen(): void {
  if (!document.fullscreenElement) {
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
  } else {
    void document.exitFullscreen?.();
  }
}

/* ---------- Bootstrap ---------- */

const canvas = document.getElementById("screen") as HTMLCanvasElement | null;
if (!canvas) throw new Error("#screen canvas bulunamadı");

// Pixel fontu garantiye al; döngü zaten sürekli yeniden çiziyor.
void document.fonts?.load('10px "Press Start 2P"');

const game = new SnakeGame(canvas);
window.addEventListener("resize", () => game.resize());
window.addEventListener("gamepadconnected", () => sfx.select());

let lastTime = performance.now();
function loop(t: number): void {
  const dt = Math.min(t - lastTime, 100);
  lastTime = t;

  game.poll(t);
  game.update(dt);
  game.draw(isAnyGamepadConnected());

  requestAnimationFrame(loop);
}

function isAnyGamepadConnected(): boolean {
  const pads = navigator.getGamepads?.() ?? [];
  return Array.from(pads).some((pad) => pad !== null);
}

requestAnimationFrame(loop);

// Hot-reload sırasında eski dinleyicileri temizle
if (import.meta.hot) {
  import.meta.hot.dispose(() => game.dispose());
}
